import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useRouteMatch } from "react-router-dom";
import { withProcedureWraper } from "../../..";
import { getEnv } from "../../../../../AppConfigs";
import { Routes } from "../../../../../AppRoutes";
import { Icon } from "../../../../../components/icon";
import { approverType } from "../../../../../constants/itCodeRequestConstants";
import { ExternalSecurityControlCardRequestService } from "../../../../../services/company-office/ExternalSecurityControlCardService";
import { useSelector } from "../../../../../store/store";
import { ProcessContextProvider, ProcessContextType } from "../../../process-context";
import SecurityCardUnitsInformation from "./information";
import SecurityCardUnitsProgress from "./progress";
import { useQueryClient } from "@tanstack/react-query";

export type ExtraStateContext = {
  isLongTerm: boolean;

  isProvide: boolean;
  isRenew: boolean;
  isEvict: boolean;
  pickedItHead: any;
  onApproverChange: (selectedOptions: any) => void;
};

export const siteUrl = getEnv("REACT_APP_APPROVER_SECURITY_CONTROL_CARD_TO_EXTERNAL_REQUEST_URL");

export const detailSecurityCardUnitsPage: FC = withProcedureWraper(() => {
  /**
   * Selectors
   */
  const queryClient = useQueryClient();
  const user = useSelector((state) => state.user);
  const currentUserName = user?.current?.username;

  const history = useHistory();
  const { id } = useRouteMatch().params as any;

  /**
   * States
   */
  const [securityCardExternalRequest, setItSecurityCardExternalRequest] = useState(null as any);
  const [comment, setComment] = useState("");
  const [approvalType, setApprovalType] = useState(0);
  const [approvalStep, setApprovalStep] = useState(approverType.approver);
  const [pickedItHead, setPickedItHead] = useState<any>(null);

  const onApproverChange = useCallback((selectedOptions: any) => {
    setPickedItHead(selectedOptions ? selectedOptions.value : "");
  }, []);

  const checkApproverType = useCallback(() => {
    const { eApprovedBy, eApprovedByITLead, eAssignee } = securityCardExternalRequest.fields;
    const approverEmail = eApprovedBy ? eApprovedBy.split("|")[0] : "";
    const approvedByITLeadEmail = eApprovedByITLead ? eApprovedByITLead.split("|")[0] : "";
    const assigneeEmail = eAssignee ? eAssignee.split("|")[0] : "";

    if (currentUserName.toLowerCase() === approverEmail.toLowerCase()) {
      setApprovalType(approverType.approver);
      return;
    }

    if (currentUserName.toLowerCase() === approvedByITLeadEmail.toLowerCase()) {
      setApprovalType(approverType.itlead);
      return;
    }

    if (currentUserName.toLowerCase() === assigneeEmail.toLowerCase()) {
      setApprovalType(approverType.assignee);
      return;
    }
  }, [currentUserName, securityCardExternalRequest?.fields]);

  const checkApproverStep = useCallback(() => {
    const { eApprovedStatus, eApprovedByITLeadStatus, eAssigneeStatus } = securityCardExternalRequest.fields;
    if (!eApprovedStatus) {
      setApprovalStep(approverType.approver);
      return;
    }
    if (!eApprovedByITLeadStatus) {
      setApprovalStep(approverType.itlead);
      return;
    }
    if (!eAssigneeStatus) {
      setApprovalStep(approverType.assignee);
      return;
    }
  }, [securityCardExternalRequest?.fields]);

  const handleUpdatePin = useCallback(async (isPin: boolean) => {
    try {
      if (!securityCardExternalRequest) return;
      const pins: any[] = securityCardExternalRequest.fields.ePins ? JSON.parse(securityCardExternalRequest.fields.ePins) : [];
      const index = pins.findIndex((pin: string) => pin === currentUserName?.toLowerCase());
      if (!isPin && index < 0) return;
      if (isPin && index > -1) return;
      if (!isPin && index > -1) pins.splice(index, 1);
      if (isPin && index < 0) pins.push(currentUserName?.toLowerCase());

      const fields = {
        ePins: JSON.stringify(pins),
      };

      await ExternalSecurityControlCardRequestService.updateExternalSecurityControlCardRequest(id, fields);
      queryClient.invalidateQueries({ queryKey: ["security-card-units-requests"] });
    } catch (error) {
      console.log(error);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserName, id, securityCardExternalRequest])

  const isPin = useCallback(() => {
    if (!securityCardExternalRequest) return false;
    const pins: any[] = securityCardExternalRequest.fields.ePins ? JSON.parse(securityCardExternalRequest.fields.ePins) : [];
    return pins.findIndex((pin: string) => pin === currentUserName?.toLowerCase()) >= 0;
  }, [currentUserName, securityCardExternalRequest])

  useEffect(() => {
    ExternalSecurityControlCardRequestService.getExternalSecurityControlCardRequest(id)
      .then((res) => {
        setItSecurityCardExternalRequest(res);
        // if (res.fields) {
        //   setComment(res.fields.eApprovalComments);
        // }
      })
      .catch((err) => console.log(err.message));
  }, [id]);

  useEffect(() => {
    if (securityCardExternalRequest) {
      checkApproverType();
      checkApproverStep();
    }
  }, [checkApproverStep, checkApproverType, securityCardExternalRequest]);

  const ctx = useMemo<ProcessContextType<ExtraStateContext>>(
    () => ({
      Request: securityCardExternalRequest,
      approvalStep,
      approvalType,
      comment,
      setComment,

      /**
       * Extra props
       */
      isLongTerm: securityCardExternalRequest?.fields.eStage === "longTerm",
      isProvide: securityCardExternalRequest?.fields.eSuggestion === "provide",
      isEvict: securityCardExternalRequest?.fields.eSuggestion === "evict",
      isRenew: securityCardExternalRequest?.fields.eSuggestion === "renew",
      pickedItHead,
      onApproverChange,
      updatePin: handleUpdatePin,
      isPin: isPin,
    }),
    [securityCardExternalRequest, approvalStep, approvalType, comment, pickedItHead, onApproverChange, handleUpdatePin, isPin]
  );

  const handleGoBack = () => {
    if (history.action === 'PUSH')
      history.goBack();
    else
      history.push(Routes.ProcessSecurityControlCardsToExternalUnits.path)
  }

  return (
    <div className="ItCodeGrantedDetailPage pd15">
      <div className="PageHead">
        <div className="title">
          <div
            className="btnBack"
            onClick={handleGoBack}
          >
            <Icon.ArrowLeft />
          </div>
          {"Xem Xét Yêu Cầu Duyệt"}
        </div>
      </div>

      <ProcessContextProvider context={ctx}>
        <SecurityCardUnitsProgress />
        <SecurityCardUnitsInformation />
      </ProcessContextProvider>
    </div>
  );
});
